"use client";

import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface CollapsibleOption {
  id: string | number;
  label: string;
  value?: string | number;
  icon?: React.ReactNode;
  onClick?: () => void;
}

interface CollapsibleSection {
  title: string;
  description?: string;
  content: React.ReactNode;
}

interface CollapsibleProps {
  title?: string;
  sections?: CollapsibleSection[];
  items?: CollapsibleOption[];
  children?: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
  onItemClick?: (item: CollapsibleOption) => void;
  renderItem?: (item: CollapsibleOption) => React.ReactNode;
  storageKey?: string;
}

export const Collapsible = ({
  title,
  sections = [],
  items = [],
  children,
  defaultOpen = false,
  className = "",
  titleClassName = "",
  contentClassName = "",
  onItemClick,
  renderItem,
  storageKey = "collapsible-state",
}: CollapsibleProps) => {
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [mounted, setMounted] = useState(false);

  // Inicializar estado desde localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          setOpenStates(JSON.parse(saved));
        } catch {
          const defaultStates = sections.reduce<Record<string, boolean>>(
            (acc, _, idx) => {
              acc[idx.toString()] = defaultOpen;
              return acc;
            },
            {},
          );
          setOpenStates(defaultStates);
        }
      } else {
        const defaultStates = sections.reduce<Record<string, boolean>>(
          (acc, _, idx) => {
            acc[idx.toString()] = defaultOpen;
            return acc;
          },
          {},
        );
        setOpenStates(defaultStates);
      }
      setMounted(true);
    }
  }, [sections.length, defaultOpen, storageKey]);

  // Guardar estado en localStorage cuando cambia
  useEffect(() => {
    if (mounted && typeof window !== "undefined") {
      localStorage.setItem(storageKey, JSON.stringify(openStates));
    }
  }, [openStates, storageKey, mounted]);

  const toggleSection = (index: string) => {
    setOpenStates((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleItemClick = (item: CollapsibleOption) => {
    item.onClick?.();
    onItemClick?.(item);
  };

  // Si hay secciones, renderizar como acordeón múltiple
  if (sections.length > 0 && mounted) {
    return (
      <div className={cn(" rounded-lg", className)}>
        {sections.map((section, index) => (
          <div key={index} className={index > 0 ? "border-t" : ""}>
            {/* Header */}
            <button
              onClick={() => toggleSection(index.toString())}
              className={cn(
                "w-full flex items-center justify-between !p-2  sm:p-4 bg-white hover:bg-gray-100 transition-colors text-left font-medium",
                titleClassName,
              )}
            >
              <div className="flex items-center gap-3">
                <span className="">{section.title}</span>
                {section.description && (
                  <span className="text-sm font-normal text-slate-500 hidden sm:inline-block">
                    {section.description}
                  </span>
                )}
              </div>
              <ChevronDown
                size={20}
                className={cn(
                  "transition-transform duration-300",
                  openStates[index.toString()] && "rotate-180",
                )}
              />
            </button>

            {/* Content */}
            {openStates[index.toString()] && (
              <div
                className={cn(
                  "!pt-1  sm:px-3 sm:py-1 animate-in fade-in-0 slide-in-from-top-2 duration-200",
                  contentClassName,
                )}
              >
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Si no hay secciones, renderizar como antes (single collapsible)
  return (
    <div className={cn(className)}>
      {/* Header */}
      {title && (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "w-full flex items-center justify-between p-3 sm:p-4 bg-gray-0 hover:bg-gray-100 transition-colors text-left font-medium cursor-pointer",
            titleClassName,
          )}
        >
          <span>{title}</span>
          <ChevronDown
            size={20}
            className={cn(
              "transition-transform duration-300",
              isOpen && "rotate-180",
            )}
          />
        </button>
      )}

      {/* Content */}
      {isOpen && (
        <div
          className={cn(
            "p-3 sm:p-4 animate-in fade-in-0 slide-in-from-top-2 duration-200",
            contentClassName,
          )}
        >
          {items.length > 0 ? (
            <div className="divide-y">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="p-3 sm:p-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-center gap-2"
                >
                  {renderItem ? (
                    renderItem(item)
                  ) : (
                    <>
                      {item.icon && <span>{item.icon}</span>}
                      <span className="text-sm">{item.label}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div>{children}</div>
          )}
        </div>
      )}
    </div>
  );
};
